// src/lib/i18n.ts

export type Locale = 'en-US' | 'cs-CZ' | 'es-ES' | 'de-DE' | 'sk-SK' | 'pl-PL' | 'hu-HU' | 'uk-UA';

export const supportedLocales: Locale[] = ['en-US', 'cs-CZ', 'es-ES', 'de-DE', 'sk-SK', 'pl-PL', 'hu-HU', 'uk-UA'];
export const defaultLocale: Locale = 'en-US';

// Define a type for the dictionary structure
// This should ideally match the structure of your JSON files
// For now, using `any` for simplicity, but you should type this properly.
export type Dictionary = any; // Replace 'any' with a more specific type as your dictionaries grow

// A helper function to merge multiple dictionary files
async function mergeDictionaries(...imports: Promise<{ default: any }>[]) {
  const dictionaries = await Promise.all(imports.map(imp => imp.then(m => m.default)));
  return dictionaries.reduce((acc, dict) => ({ ...acc, ...dict }), {});
}

const dictionaries: Record<Locale, () => Promise<Dictionary>> = {
  'en-US': () => mergeDictionaries(
    import(`@/dictionaries/en-US/auth.json`),
    import(`@/dictionaries/en-US/common.json`),
    import(`@/dictionaries/en-US/dashboard.json`),
    import(`@/dictionaries/en-US/settings.json`),
    import(`@/dictionaries/en-US/locations.json`),
    import(`@/dictionaries/en-US/lpr.json`),
    import(`@/dictionaries/en-US/reservations.json`),
    import(`@/dictionaries/en-US/sidebar.json`),
    import(`@/dictionaries/en-US/phone-access-lists.json`),
    import(`@/dictionaries/en-US/location-access-settings.json`),
    import(`@/dictionaries/en-US.json`)
  ),
  'cs-CZ': () => mergeDictionaries(
    import(`@/dictionaries/cs-CZ/auth.json`),
    import(`@/dictionaries/cs-CZ/common.json`),
    import(`@/dictionaries/cs-CZ/dashboard.json`),
    import(`@/dictionaries/cs-CZ/settings.json`),
    import(`@/dictionaries/cs-CZ/locations.json`),
    import(`@/dictionaries/cs-CZ/lpr.json`),
    import(`@/dictionaries/cs-CZ/reservations.json`),
    import(`@/dictionaries/cs-CZ/sidebar.json`),
    import(`@/dictionaries/cs-CZ/phone-access-lists.json`),
    import(`@/dictionaries/cs-CZ/location-access-settings.json`),
    import(`@/dictionaries/cs-CZ.json`)
  ),
  'de-DE': () => mergeDictionaries(
    import(`@/dictionaries/de-DE/auth.json`),
    import(`@/dictionaries/de-DE/common.json`),
    import(`@/dictionaries/de-DE/dashboard.json`),
    import(`@/dictionaries/de-DE/settings.json`),
    import(`@/dictionaries/de-DE/locations.json`),
    import(`@/dictionaries/de-DE/lpr.json`),
    import(`@/dictionaries/de-DE/reservations.json`),
    import(`@/dictionaries/de-DE/sidebar.json`),
    import(`@/dictionaries/de-DE/phone-access-lists.json`),
    import(`@/dictionaries/de-DE/location-access-settings.json`),
    import(`@/dictionaries/de-DE.json`)
  ),
  'es-ES': () => mergeDictionaries(
    import(`@/dictionaries/es-ES/auth.json`),
    import(`@/dictionaries/es-ES/common.json`),
    import(`@/dictionaries/es-ES/dashboard.json`),
    import(`@/dictionaries/es-ES/settings.json`),
    import(`@/dictionaries/es-ES/locations.json`),
    import(`@/dictionaries/es-ES/lpr.json`),
    import(`@/dictionaries/es-ES/reservations.json`),
    import(`@/dictionaries/es-ES/sidebar.json`),
    import(`@/dictionaries/es-ES/phone-access-lists.json`),
    import(`@/dictionaries/es-ES/location-access-settings.json`),
    import(`@/dictionaries/es-ES.json`)
  ),
  'sk-SK': () => mergeDictionaries(
    import(`@/dictionaries/sk-SK/auth.json`),
    import(`@/dictionaries/sk-SK/common.json`),
    import(`@/dictionaries/sk-SK/dashboard.json`),
    import(`@/dictionaries/sk-SK/settings.json`),
    import(`@/dictionaries/sk-SK/locations.json`),
    import(`@/dictionaries/sk-SK/lpr.json`),
    import(`@/dictionaries/sk-SK/reservations.json`),
    import(`@/dictionaries/sk-SK/sidebar.json`),
    import(`@/dictionaries/sk-SK/phone-access-lists.json`),
    import(`@/dictionaries/sk-SK/location-access-settings.json`),
    import(`@/dictionaries/sk-SK.json`)
  ),
  'pl-PL': () => mergeDictionaries(
    import(`@/dictionaries/pl-PL/auth.json`),
    import(`@/dictionaries/pl-PL/common.json`),
    import(`@/dictionaries/pl-PL/dashboard.json`),
    import(`@/dictionaries/pl-PL/settings.json`),
    import(`@/dictionaries/pl-PL/locations.json`),
    import(`@/dictionaries/pl-PL/lpr.json`),
    import(`@/dictionaries/pl-PL/reservations.json`),
    import(`@/dictionaries/pl-PL/sidebar.json`),
    import(`@/dictionaries/pl-PL/phone-access-lists.json`),
    import(`@/dictionaries/pl-PL/location-access-settings.json`),
    import(`@/dictionaries/pl-PL.json`)
  ),
  'hu-HU': () => mergeDictionaries(
    import(`@/dictionaries/hu-HU/auth.json`),
    import(`@/dictionaries/hu-HU/common.json`),
    import(`@/dictionaries/hu-HU/dashboard.json`),
    import(`@/dictionaries/hu-HU/settings.json`),
    import(`@/dictionaries/hu-HU/locations.json`),
    import(`@/dictionaries/hu-HU/lpr.json`),
    import(`@/dictionaries/hu-HU/reservations.json`),
    import(`@/dictionaries/hu-HU/sidebar.json`),
    import(`@/dictionaries/hu-HU/phone-access-lists.json`),
    import(`@/dictionaries/hu-HU/location-access-settings.json`),
    import(`@/dictionaries/hu-HU.json`)
  ),
  'uk-UA': () => mergeDictionaries(
    import(`@/dictionaries/uk-UA/auth.json`),
    import(`@/dictionaries/uk-UA/common.json`),
    import(`@/dictionaries/uk-UA/dashboard.json`),
    import(`@/dictionaries/uk-UA/settings.json`),
    import(`@/dictionaries/uk-UA/locations.json`),
    import(`@/dictionaries/uk-UA/lpr.json`),
    import(`@/dictionaries/uk-UA/reservations.json`),
    import(`@/dictionaries/uk-UA/sidebar.json`),
    import(`@/dictionaries/uk-UA/phone-access-lists.json`),
    import(`@/dictionaries/uk-UA/location-access-settings.json`),
    import(`@/dictionaries/uk-UA.json`)
  ),
};

export const getDictionary = async (locale: Locale): Promise<Dictionary> => {
  const loadFn = dictionaries[locale] || dictionaries[defaultLocale];
  return loadFn();
};

export const isValidLocale = (locale: string): locale is Locale => {
  return supportedLocales.includes(locale as Locale);
};
